import { appOverride, appVariant } from '@util/AppUtils';
import { Environment } from '@util/Environment';
import { translate } from '@util/Localization';
import { isDirectToPayRoute } from '@util/RouterUtils';
import { App } from 'hydra-data-types';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { isDefined } from 'ts-utils';
import { formatHcpDetails, getHcpDetailsFromUrl } from '../../../config/AssessmentUtils';
import { Color } from '../../../config/Color';
import { SharedScreenName } from '../../../config/FlowTypes';
import { useAppStore } from '../../../store/Store';
import * as images from '../../assets/Images';
import * as logos from '../../assets/images/Logos';
import { useFlowNavigator } from '../../flows/FlowNavigation';
import { Body, BodyXS, Emphasis, bodyFontStyle } from '../../styles/Typography';
import { CountdownTimer } from './CountdownTimer';
import { MoneyBackGuarantee as BaseMoneyBackGuarantee } from './MoneyBackGuarantee';

export const COUNTDOWN_FROM_SECONDS = 15 * 60;

export const ProductDetails: FunctionComponent = () => {
  const referralHcpDetails = getHcpDetailsFromUrl();
  const valuePropositionArray = translate('payment.productDetails').split('\n');

  const doctorImage = appVariant({
    nerva: images.drPetersCredentials,
    meno: images.drGary,
    quit: images.drGary,
    pain: null,
  });

  const isDirectToPay = isDirectToPayRoute();
  const navigation = useFlowNavigator();
  const paymentLoading = useAppStore((state) => state.purchase.payment.loading);

  const goBackToResults = () => {
    // Can't route back from /start. Leaving mid-payment unmounts the form that moves them on once
    // the payment succeeds.
    if (!isDirectToPay && !paymentLoading) {
      navigation.goTo(SharedScreenName.Results);
    }
  };

  // When we added App.Pain, we went from having two options (Nerva & non-Nerva)
  // to having three. When there are two options, an if-then block is arguably preferrable over a switch case
  // but with three or more, a switch case block is arguable preferred over an if-then block
  // The switch block will also allow for simpler addition of new apps, with the new code clearly separated

  let infoBlock: React.ReactElement | undefined;
  switch (Environment.APP) {
    case App.Pain:
      infoBlock = (
        <div
          style={{
            paddingBottom: '2em',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Body>Created with leading researchers from Neuroscience Research Australia. </Body>
          <MarginImage
            style={{ flexGrow: '1', marginRight: '175px', maxWidth: '120px' }}
            src={logos.neura}
            alt="NeuRA Logo"
          />
        </div>
      );
      break;
    default:
      // We should split this into two separate cases, one for Baylor and one for Monash, but
      // I am not sure which apps Baylor univerity is associated with, so I have left the original code:
      infoBlock = appOverride({
        nerva: (
          <div style={{ paddingBottom: '2em' }}>
            <Body>Created with</Body>
            {isDefined(doctorImage) && (
              <img
                src={doctorImage}
                alt={translate('doctorShort')}
                style={{
                  display: 'block',
                  width: '100%',
                  maxWidth: '300px',
                  height: 'auto',
                  margin: '8px 0',
                }}
              />
            )}
          </div>
        ),
        default: (
          <div style={{ paddingBottom: '2em' }}>
            {doctorImage === null ? null : (
              <FloatLeftImage
                src={doctorImage}
                width="74"
                height="74"
                alt={translate('doctorShort')}
              />
            )}
            <Body>Created with {translate('doctorShort')}</Body>
            <div style={{ display: 'inline-block' }}>
              {Environment.APP !== App.Nerva ? (
                <MarginImage
                  src={logos.baylor}
                  alt="Baylor University Logo"
                  style={{ margin: '10px 1em 0.5em 0' }}
                  width="120"
                  height="22"
                />
              ) : (
                <>
                  <MarginImage
                    src={logos.monash}
                    alt="Monash University Logo"
                    style={{ margin: '10px 1em 0.5em 0' }}
                    width="85"
                    height="25"
                  />
                  <MarginImage src={logos.mindGut} alt="Mind + Gut Clinic Logo" width="40" />
                </>
              )}
            </div>
          </div>
        ),
      });
  }

  const productTitle = 'What you’ll get:';

  return (
    <>
      {isDefined(referralHcpDetails) ? (
        <>
          <BodyXS>
            <Emphasis>Referred by {formatHcpDetails(referralHcpDetails)}</Emphasis>
          </BodyXS>
          <BodyXS bold>{productTitle}</BodyXS>
        </>
      ) : (
        <BodyXS>
          <Emphasis>{productTitle}</Emphasis>
        </BodyXS>
      )}
      <Bullet>
        {valuePropositionArray.map((valueProp, index) => (
          <Paragraph key={index}>
            <li>{valueProp}</li>
          </Paragraph>
        ))}
      </Bullet>
      {infoBlock}
      {isDirectToPay ? ( // Don't show the CountdownTimer when returning to /start
        <MoneyBackGuarantee />
      ) : (
        <CountdownTimer
          initSeconds={COUNTDOWN_FROM_SECONDS}
          message="Your personalized program has been saved for the next 15 minutes"
          onZero={goBackToResults}
        />
      )}
    </>
  );
};

const Bullet = styled.ul`
  text-align: left;
  list-style: none;
  margin-left: 1.3em;
  padding-inline-start: 0px;

  li {
    padding-left: 0;
    text-indent: -1.3em;
    ${bodyFontStyle}
  }
  li::before {
    content: '•';
    margin-right: ${({ theme }) => theme.spacing._10px};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    color: ${appOverride({ nerva: Color.content.default, default: Color.decorative.onAccent })};
  }
`;

const Paragraph = styled(BodyXS)`
  margin: 0px;
`;

const FloatLeftImage = styled.img`
  margin: 0 0.5em 0 0;
  padding-left: 0;
  float: left;
`;

const MarginImage = styled.img`
  margin: 0px;
`;

const MoneyBackGuarantee = styled(BaseMoneyBackGuarantee)`
  margin: 24px -16px;
`;
